import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { promptLogin } from "@/lib/authRedirect";
import { toast } from "sonner";
import { ChevronLeft, Loader2, Check } from "lucide-react";
import SEO from "@/components/site/SEO";

/**
 * Jelajah Ruang — directory of all 16 Ruang.
 * Grouped into "SEKOLAH — SESUAI JENJANG" (Grup A) and "PENDIDIKAN & KEHIDUPAN ANAK" (Grup B).
 * Each row: icon, name, `{member_count} anggota · {topic_count} topik`,
 * plus a Gabung / ✓ Tergabung toggle.
 */
export default function RuangDirectory() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [groups, setGroups] = useState({ grup_a: [], grup_b: [] });
  const [joined, setJoined] = useState(() => new Set());
  const [pendingId, setPendingId] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get("/ruang"),
          user ? api.get("/me/memberships").catch(() => ({ data: { ruang_ids: [] } })) : Promise.resolve({ data: { ruang_ids: [] } }),
        ]);
        setGroups({ grup_a: r1.data.grup_a?.items || [], grup_b: r1.data.grup_b?.items || [] });
        setJoined(new Set(r2.data.ruang_ids || []));
      } catch {
        toast.error("Gagal memuat Ruang.");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const toggleJoin = async (r) => {
    if (!user) {
      promptLogin(nav);
      return;
    }
    const isJoined = joined.has(r.ruang_id);
    setPendingId(r.ruang_id);
    // Optimistic
    setJoined((prev) => {
      const nx = new Set(prev);
      isJoined ? nx.delete(r.ruang_id) : nx.add(r.ruang_id);
      return nx;
    });
    try {
      await api.post(`/ruang/${r.ruang_id}/${isJoined ? "leave" : "join"}`);
    } catch (e) {
      // Revert
      setJoined((prev) => {
        const nx = new Set(prev);
        isJoined ? nx.add(r.ruang_id) : nx.delete(r.ruang_id);
        return nx;
      });
      toast.error(e?.response?.data?.detail || "Gagal memperbarui keanggotaan.");
    } finally {
      setPendingId(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center bg-brand-alabaster">
        <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-alabaster" data-testid="ruang-directory-page">
      <SEO title="Jelajah Ruang — The School Reports" description="Gabung Ruang untuk dapat update di beranda & ketemu orang tua lain." />

      {/* Header — dark navy */}
      <div className="bg-brand-primary text-white">
        <div className="max-w-2xl mx-auto px-5 py-6 flex items-center gap-3">
          <button onClick={() => nav(-1)} aria-label="Kembali" data-testid="ruang-directory-back" className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </button>
          <h1 className="font-serif-display text-2xl">Jelajah Ruang</h1>
        </div>
        <div className="max-w-2xl mx-auto px-5 pb-6 text-sm text-white/80">
          Gabung Ruang untuk dapat update di beranda &amp; ketemu orang tua lain.
        </div>
      </div>

      {/* Groups */}
      <div className="max-w-2xl mx-auto px-5 py-8 space-y-10">
        <GroupSection title="SEKOLAH — SESUAI JENJANG" count={groups.grup_a.length}>
          {groups.grup_a.map((r) => (
            <RuangRow key={r.ruang_id} r={r} joined={joined.has(r.ruang_id)} pending={pendingId === r.ruang_id} onToggle={() => toggleJoin(r)} />
          ))}
        </GroupSection>

        <GroupSection title="PENDIDIKAN & KEHIDUPAN ANAK" count={groups.grup_b.length}>
          {groups.grup_b.map((r) => (
            <RuangRow key={r.ruang_id} r={r} joined={joined.has(r.ruang_id)} pending={pendingId === r.ruang_id} onToggle={() => toggleJoin(r)} />
          ))}
        </GroupSection>
      </div>
    </div>
  );
}

function GroupSection({ title, count, children }) {
  return (
    <section>
      <div className="flex items-center justify-between mb-4">
        <p className="eyebrow text-brand-text-muted">{title}</p>
        <span className="text-xs text-brand-text-muted uppercase tracking-wider">{count} Ruang</span>
      </div>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function RuangRow({ r, joined, pending, onToggle }) {
  return (
    <div className="flex items-center gap-4 rounded-2xl bg-white border border-brand-border p-4 shadow-sm">
      <Link to={`/forum/ruang/${r.slug}`} className="flex items-center gap-4 flex-1 min-w-0" data-testid={`ruang-row-link-${r.slug}`}>
        <span className="w-11 h-11 rounded-2xl bg-brand-muted flex items-center justify-center text-2xl shrink-0" aria-hidden>
          {r.icon}
        </span>
        <span className="min-w-0">
          <span className="block font-semibold text-brand-text leading-tight">{r.name}</span>
          <span className="block text-xs text-brand-text-muted mt-1">
            {formatCount(r.member_count)} anggota · {formatCount(r.topic_count ?? r.discussion_count ?? 0)} topik
          </span>
        </span>
      </Link>
      <button
        onClick={onToggle}
        disabled={pending}
        data-testid={`ruang-toggle-${r.slug}`}
        className={`shrink-0 h-9 px-4 rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
          joined
            ? "bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100"
            : "bg-white text-brand-accent border border-brand-accent hover:bg-brand-accent hover:text-white"
        } disabled:opacity-50`}
      >
        {joined ? (
          <span className="inline-flex items-center gap-1"><Check className="w-3.5 h-3.5" strokeWidth={3} /> Tergabung</span>
        ) : (
          "Gabung"
        )}
      </button>
    </div>
  );
}

function formatCount(n) {
  const num = Number(n || 0);
  if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, "")}rb`;
  return String(num);
}
